import React, { useState } from 'react';
import TabNavigation from './components/common/TabNavigation';
import StatusMessage from './components/common/StatusMessage';
import GettingStarted from './components/getting-started/GettingStarted';
import CustomerInfoSection from './components/context/CustomerInfoSection';
import ProposalTeamSection from './components/context/ProposalTeamSection';
import IPAContentSection from './components/context/IPAContentSection';
import ProcessTable from './components/process/ProcessTable';
import ROISection from './components/roi/ROISection';
import ReportDownloadPanel from './components/report/ReportDownloadPanel';
import { generateCustomerId } from './services/contextService';
import { defaultContext } from './utils/constants';

function App() {
  const [activeTab, setActiveTab] = useState('getting-started');
  const [customerId, setCustomerId] = useState(null);
  const [contextData, setContextData] = useState(defaultContext);
  const [processes, setProcesses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const sanitizeContext = (ctx) => {
    // Ensure numeric fields are actually numbers, not strings
    const numericFields = [
      'cost_per_agent',
      'professional_services_price',
      'bom_price',
      'bom_length_months',
      'monthly_usage_costs'
    ];

    const sanitized = { ...ctx };

    numericFields.forEach(field => {
      if (sanitized[field] !== undefined && sanitized[field] !== null) {
        const num = parseFloat(sanitized[field]);
        sanitized[field] = isNaN(num) ? 0 : num;
      }
    });

    return sanitized;
  };

  const tabs = [
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'context', label: 'Customer Context' },
    { id: 'team', label: 'Proposal Team' },
    { id: 'content', label: 'IPA Content' },
    { id: 'processes', label: 'Process Assessment' },
    { id: 'roi', label: 'ROI' },
    { id: 'report', label: 'Report Generation' }
  ];

  const handleSaveContext = async () => {
    if (!contextData.customer) {
      setStatus({ type: 'error', message: 'Customer name is required' });
      return;
    }

    setSaving(true);
    setStatus(null);

    try {
      const id = generateCustomerId(contextData.customer);

      // Save to sessionStorage (clears when tab closes)
      sessionStorage.setItem(`ipa-context-${id}`, JSON.stringify(contextData));
      sessionStorage.setItem('ipa-last-customer-id', id);

      setCustomerId(id);

      setStatus({
        type: 'success',
        message: `Context saved for this session! Customer ID: ${id}`
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to save context: ${error.message}`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFilesUploaded = ({ customerId: id, contextData: ctx, processes: procs }) => {
    if (id) {
      setCustomerId(id);
    }
    if (ctx) {
      // Sanitize to ensure numeric fields are numbers
      const sanitizedContext = sanitizeContext(ctx);
      setContextData(sanitizedContext);
    }
    if (procs) {
      setProcesses(procs);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white p-6 shadow-lg">
        <div className="container mx-auto">
          <h1 className="text-3xl font-bold">IPA Report Generator</h1>
          <p className="text-blue-100 mt-2">
            Interaction Process Automation Assessment Tool
          </p>
          {customerId && (
            <p className="text-blue-200 text-sm mt-2">
              Customer ID: <span className="font-mono bg-blue-700 px-2 py-1 rounded">{customerId}</span>
            </p>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Global Status Message */}
        {status && (
          <div className="mb-6">
            <StatusMessage
              type={status.type}
              message={status.message}
              onClose={() => setStatus(null)}
            />
          </div>
        )}

        {/* Tab Content - Getting Started */}
        <div className={activeTab === 'getting-started' ? 'block' : 'hidden'}>
          <GettingStarted
            onFilesUploaded={handleFilesUploaded}
            setActiveTab={setActiveTab}
          />
        </div>

        {/* Tab Content - Customer Context */}
        <div className={activeTab === 'context' ? 'block' : 'hidden'}>
          <CustomerInfoSection
            context={contextData}
            setContext={setContextData}
            onSave={handleSaveContext}
            saving={saving}
            status={status}
          />
        </div>

        {/* Tab Content - Proposal Team */}
        <div className={activeTab === 'team' ? 'block' : 'hidden'}>
          <ProposalTeamSection
            context={contextData}
            setContext={setContextData}
            onSave={handleSaveContext}
            saving={saving}
          />
        </div>

        {/* Tab Content - IPA Content */}
        <div className={activeTab === 'content' ? 'block' : 'hidden'}>
          <IPAContentSection
            context={contextData}
            setContext={setContextData}
            onSave={handleSaveContext}
            saving={saving}
          />
        </div>

        {/* Tab Content - Process Assessment */}
        <div className={activeTab === 'processes' ? 'block' : 'hidden'}>
          <ProcessTable
            customerId={customerId}
            processes={processes}
            setProcesses={setProcesses}
            contextData={contextData}
          />
        </div>

        {/* Tab Content - ROI */}
        <div className={activeTab === 'roi' ? 'block' : 'hidden'}>
          <ROISection
            processes={processes}
            setProcesses={setProcesses}
            onProcessChange={(index, updatedProcess) => {
              const newProcesses = [...processes];
              newProcesses[index] = updatedProcess;
              setProcesses(newProcesses);
            }}
            contextData={contextData}
            customerId={customerId}
          />
        </div>

        {/* Tab Content - Report Generation */}
        <div className={activeTab === 'report' ? 'block' : 'hidden'}>
          <ReportDownloadPanel
            customerId={customerId}
            contextData={contextData}
            processes={processes}
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 p-6 mt-12">
        <div className="container mx-auto text-center">
          <p>&copy; 2025 Presidio - IPA Report Generator</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
